import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Params, SCREEN_SIZE, USERSROLE } from 'types';
import { MainHeaderContainer, MainContainer } from 'layouts/components';
import {
  Button,
  ContentSwitcher,
  Switch,
  Modal
} from 'carbon-components-react';
import { ListView, MapView, SearchFilter } from './components';
import { ListBulleted16, Map16 } from '@carbon/icons-react';
import { currentUser, selectScreenSize } from 'redux/session/selectors';
import { selectAnnouncements } from 'redux/announcement/selectors';
import { removeAllAnnouncements } from 'redux/announcement';
import {
  selectEnquirySearchFilters,
  selectForwardEnquiryExternalId,
  selectForwardEnquiryIsListView,
  selectForwardEnquiryDateRangeText,
  fetchEnquiryHistory,
  clearAllEnquiryState,
  clearEnquiries,
  fetchEnquiryById,
  fetchResetLodgeEnquiry
} from 'redux/enquiries';
import { selectAdminRangeText } from 'redux/dashboard/selectors';
import PropTypes from 'prop-types';
import { fetchLodgeEnquiryInit } from 'redux/lodgeEnquiry';
import { selectOrganisationTenants } from 'redux/member/selectors';
import { fetchOrgTenantsStart } from 'redux/member/action';
import { useComponentMount } from 'utilities/hooks.util';
import { useParams } from 'react-router';
import { EnquiryDetailsNew } from '../enquiryDetailsNew';
import { useDeviceType } from 'customHooks/useDeviceType';

const filterStringReducer = (accumulator: string[], currentValue: any) => {
  const [key, value] = currentValue;

  switch (key) {
    case 'status':
      if (value.length > 0) accumulator.push(`${key}:${value.join('|')}`);
      return accumulator;
    case 'userReference':
      if (value) accumulator.push(`userReference:%25${value}%25`);
      return accumulator;
    case 'address':
      if (value) accumulator.push(`address:%25${value}%25`);
      return accumulator;
    case 'requestorFullName':
      if (value) accumulator.push(`requestorFullName:%25${value}%25`);
      return accumulator;
    case 'requestorEmail':
      if (value) accumulator.push(`requestorEmail:%25${value}%25`);
      return accumulator;
    case 'isSandboxTest':
      if (!value) accumulator.push(`${key}:${value}`);
      return accumulator;
    default:
      if (value) accumulator.push(`${key}:${value}`);
      return accumulator;
  }
};

interface ParamTypes {
  enquiryId?: string;
}

export const EnquiryHistory: React.FC<Props> = ({ isFullSet }) => {
  // INIT
  const location = useLocation();
  const history = useHistory();
  let { enquiryId } = useParams<ParamTypes>();
  if (!enquiryId) {
    const queryParams = new URLSearchParams(location.search);
    enquiryId = queryParams.get('enquiryId') || undefined;
  }
  const PAGE_LIMIT = 10;
  const PAGE_OFFSET = 0;
  const dispatch = useDispatch();
  const user: any = useSelector(currentUser);
  const screenSize = useSelector(selectScreenSize);
  const searchFillter = useSelector(selectEnquirySearchFilters);
  const forwardEnquiryExternalId = useSelector(selectForwardEnquiryExternalId);
  const forwardIsListView = useSelector(selectForwardEnquiryIsListView);
  const dashboardAdminRangeText = useSelector(selectAdminRangeText);
  const forwardEnquiryDateRangeText = useSelector(
    selectForwardEnquiryDateRangeText
  );
  const announcements = useSelector(selectAnnouncements);
  const orgTenants = useSelector(selectOrganisationTenants);
  const [pageOffset, setPageOffset] = useState<number>(PAGE_OFFSET);
  const [filterString, setFilterString] = useState<string>('');
  const [isPannelExpanded, setIsPannelExpanded] = useState<boolean>(false);
  const [isListView, setIsListView] = useState<boolean>(false);
  const [enquiryList, setEnquiryList] = useState<any[]>([]);
  const [isFirstFilterReady, setIsFirstFilterReady] = useState<boolean>(
    isFullSet ? (forwardEnquiryExternalId ? true : false) : true
  );
  const [orderBy, setOrderBy] = useState<string>('-createdAt');
  const [userId, setUserId] = useState<number | undefined>();
  const [refreshEnquiryDetails, setRefreshEnquiryDetails] = useState<boolean>(
    false
  );
  const [initialDateRangeText, setInitialDateRangeSet] = useState<string>(
    dashboardAdminRangeText
  );

  const [isDownloadPdf, setIsDownloadPdf] = useState(false);
  const [isDownloadZip, setIsDownloadZip] = useState(false);

  const { isMobileOrTablet } = useDeviceType();

  const overallContainerRef = useRef(null);

  const onClickCloseButton = () => {
    if (isDownloadPdf || isDownloadZip) {
      // if body have other modal open on top
      setIsDownloadPdf(false); // reset flag
      setIsDownloadZip(false); // reset flag
      return;
    }
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.has('enquiryId')) {
      queryParams.delete('enquiryId');
      history.replace({
        search: queryParams.toString()
      });
    }
    setRefreshEnquiryDetails(true);
    setIsPannelExpanded(!isPannelExpanded);
    dispatch(fetchResetLodgeEnquiry());
  };
  const rightPanelExpandHandler = () => {
    setIsPannelExpanded(!isPannelExpanded);
  };

  const loadMoreListHandler = () => {
    setPageOffset(pageOffset + PAGE_LIMIT);
  };

  useEffect(() => {
    if (forwardEnquiryDateRangeText) {
      setInitialDateRangeSet(forwardEnquiryDateRangeText);
    } else {
      setInitialDateRangeSet(dashboardAdminRangeText);
    }
  }, [dashboardAdminRangeText, forwardEnquiryDateRangeText]);

  useEffect(() => {
    const queryParams: Params = {
      fields: `id%2CcreatedAt%2CexternalId%2CuserReference%2Cstatus%2CisSandboxTest${
        isFullSet ? '%2CuserId' : ''
      }`,
      include: `Address${isFullSet ? '%2CReferral%2CUser' : ''}`,
      orderBy,
      limit: PAGE_LIMIT,
      offset: pageOffset,
      filter: filterString
    };

    const canFetchEnquiryHistory = isListView && queryParams && filterString;

    if (canFetchEnquiryHistory) {
      dispatch(fetchEnquiryHistory(queryParams));
    }
  }, [dispatch, pageOffset, filterString, isFullSet, orderBy, isListView]);

  useEffect(() => {
    setPageOffset(PAGE_OFFSET);
    setEnquiryList([]);
  }, [orderBy]);

  useEffect(() => {
    const extraFilter = !isFullSet ? { userId: userId || '' } : {};
    const filterArr = Object.entries({
      ...searchFillter,
      ...extraFilter
    });
    setPageOffset(PAGE_OFFSET);
    setEnquiryList([]);
    userId &&
      searchFillter &&
      setFilterString(filterArr.reduce(filterStringReducer, []).join(','));
  }, [userId, searchFillter, isFullSet]);

  useEffect(() => {
    user?.id && setUserId(user.id);
  }, [user]);

  useEffect(() => {
    if (!isListView) {
      setEnquiryList([]);
      setPageOffset(PAGE_OFFSET);
      dispatch(clearEnquiries());
    }
  }, [isListView, dispatch]);

  useEffect(() => {
    if (
      user.role === USERSROLE.DBYDADMIN &&
      !user?.Organisation?.tenantId &&
      !Array.isArray(orgTenants)
    ) {
      dispatch(fetchOrgTenantsStart());
    }
  }, [user, orgTenants, dispatch]);

  useEffect(() => {
    refreshEnquiryDetails && setRefreshEnquiryDetails(false);
  }, [refreshEnquiryDetails]);

  useComponentMount(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get('view') === 'list') {
      setIsListView(true);
      queryParams.delete('view');
      history.replace({
        search: queryParams.toString()
      });
    }
    forwardEnquiryExternalId && rightPanelExpandHandler();
    forwardIsListView && setIsListView(true);
    dispatch(fetchLodgeEnquiryInit());
    if (announcements?.length > 0 && announcements[0].noticeType === 'MEMBER') {
      dispatch(removeAllAnnouncements());
    }

    if (enquiryId && Number(enquiryId)) {
      dispatch(fetchEnquiryById(Number(enquiryId)));
      rightPanelExpandHandler();
    }

    return () => {
      dispatch(clearAllEnquiryState());
    };
  });

  return (
    <div
      id="enquiryHistory"
      data-testid="enquiryHistory"
      className={`container--wrapper enquiryHistory history-page${
        isFullSet ? ' is-full-set' : ''
      } ${isPannelExpanded ? 'opened-modal' : ''}`}
      ref={overallContainerRef}
    >
      <MainHeaderContainer enableHeaderGlobalBar={true} />

      <MainContainer>
        <div>
          <Modal
            open={isPannelExpanded}
            onRequestClose={onClickCloseButton}
            passiveModal
            size="lg"
            className={
              isMobileOrTablet
                ? 'enquiry-details-modal mobile-view-enquiry-details-modal'
                : 'enquiry-details-modal'
            }
            preventCloseOnClickOutside={true}
          >
            {!refreshEnquiryDetails && (
              <EnquiryDetailsNew
                isModal={true}
                onClickClose={onClickCloseButton}
                setIsDownloadPdf={setIsDownloadPdf}
                setIsDownloadZip={setIsDownloadZip}
              />
            )}
          </Modal>
        </div>

        <section
          className={`history-page--header${isListView ? ' is-list-view' : ''}`}
        >
          <div className="page--heading">
            <h4 className="bx--data-table-header__title">
              {isFullSet ? 'All' : 'My'} enquiries
            </h4>
            {screenSize !== SCREEN_SIZE.SM && (
              <p className="bx--data-table-header__description">
                Select and review details
              </p>
            )}
          </div>
          <div className="page--controls">
            <SearchFilter
              open={!isFirstFilterReady || undefined}
              disabledAllExceptApply={!isFirstFilterReady}
              onApply={() => setIsFirstFilterReady(true)}
              isFullSet={isFullSet}
              dateRangeText={
                isFullSet ? initialDateRangeText : forwardEnquiryDateRangeText
              }
              user={user}
              mainContainerRef={overallContainerRef}
              isListView={isListView}
            />
            <div className="page--switcher">
              {screenSize === SCREEN_SIZE.SM ? (
                <>
                  <Button
                    size="field"
                    className={`icon-button-switcher ${
                      !isListView ? 'icon-button-switcher--selected' : ''
                    }`}
                    hasIconOnly
                    renderIcon={Map16}
                    tooltipAlignment="center"
                    tooltipPosition="bottom"
                    iconDescription="Map view"
                    onClick={() => setIsListView(false)}
                  />
                  <Button
                    size="field"
                    className={`icon-button-switcher ${
                      isListView ? 'icon-button-switcher--selected' : ''
                    }`}
                    hasIconOnly
                    renderIcon={ListBulleted16}
                    tooltipAlignment="center"
                    tooltipPosition="bottom"
                    iconDescription="List view"
                    onClick={() => setIsListView(true)}
                  />
                </>
              ) : (
                <ContentSwitcher selectedIndex={isListView ? 1 : 0}>
                  <Switch
                    name={'map-view'}
                    text="Map view"
                    onClick={() => setIsListView(false)}
                  />
                  <Switch
                    name={'list-view'}
                    text="List view"
                    onClick={() => setIsListView(true)}
                  />
                </ContentSwitcher>
              )}
            </div>
          </div>
        </section>
        <section className="history-page--view-switcher">
          {isListView ? (
            <ListView
              isFullSet={isFullSet}
              loadMoreData={loadMoreListHandler}
              enquiryList={enquiryList}
              setEnquiryList={setEnquiryList}
              setOrderBy={setOrderBy}
              selectedId={() => rightPanelExpandHandler()}
              isPannelExpanded={isPannelExpanded}
            />
          ) : (
            <MapView
              isFullSet={isFullSet}
              selectedId={() => rightPanelExpandHandler()}
              isMapVisible={isFirstFilterReady}
              isPanelExpanded={isPannelExpanded}
            />
          )}
        </section>
      </MainContainer>
    </div>
  );
};

const propTypes = {
  isFullSet: PropTypes.bool
};

type Props = PropTypes.InferProps<typeof propTypes>;

EnquiryHistory.propTypes = propTypes;

EnquiryHistory.displayName = 'EnquiryHistory';

export default React.memo(EnquiryHistory);
